import { useState } from 'react';
import { api, getErrorMessage } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Send, Mail, MessageSquare, Phone, Link2, Copy, Check, ExternalLink } from 'lucide-react';

export default function SendPortalLinkDialog({ open, onOpenChange, booking, guest, onSuccess }) {
  const [sending, setSending] = useState(false);
  const [channels, setChannels] = useState({
    email: true,
    sms: false,
    whatsapp: false
  });
  const [result, setResult] = useState(null);

  const handleSend = async () => {
    const selectedChannels = Object.entries(channels)
      .filter(([_, selected]) => selected)
      .map(([channel]) => channel);

    if (selectedChannels.length === 0) {
      toast.error('Please select at least one channel');
      return;
    }

    setSending(true);
    try {
      const response = await api.post(`/bookings/${booking.id}/send-portal-link`, {
        booking_id: booking.id,
        channels: selectedChannels,
        base_url: window.location.origin  // Pass the current domain for portal URL generation
      });

      setResult(response.data);
      
      if (response.data.success) {
        toast.success(response.data.message || 'Portal link sent successfully!');
        if (onSuccess) onSuccess();
      } else {
        toast.error(response.data.message || 'Failed to send portal link');
      }
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to send portal link'));
    } finally {
      setSending(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Link copied to clipboard!');
  };

  const contactEmail = booking?.contact_email || guest?.email;
  const contactPhone = booking?.contact_phone || guest?.phone;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="send-portal-link-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-violet-600" />
            Send Guest Portal Link
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Guest Info */}
          <div className="bg-gray-50 rounded-lg p-3 space-y-2">
            <p className="font-medium">{guest?.name}</p>
            <p className="text-sm text-gray-600">Room: {booking?.room_number || 'Not assigned'}</p>
            {contactEmail && (
              <p className="text-sm text-gray-600 flex items-center gap-1">
                <Mail className="h-3 w-3" /> {contactEmail}
              </p>
            )}
            {contactPhone && (
              <p className="text-sm text-gray-600 flex items-center gap-1">
                <Phone className="h-3 w-3" /> {contactPhone}
              </p>
            )}
          </div>

          {/* Channel Selection */}
          <div className="space-y-3">
            <Label>Select channels to send link:</Label>
            
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-gray-50">
                <Checkbox
                  checked={channels.email}
                  onCheckedChange={(checked) => setChannels(prev => ({ ...prev, email: checked }))}
                  disabled={!contactEmail}
                  data-testid="channel-email"
                />
                <Mail className="h-4 w-4 text-gray-500" />
                <span className={!contactEmail ? 'text-gray-400' : ''}>
                  Email
                  {!contactEmail && <span className="text-xs ml-1">(no email)</span>}
                </span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-gray-50">
                <Checkbox
                  checked={channels.sms}
                  onCheckedChange={(checked) => setChannels(prev => ({ ...prev, sms: checked }))}
                  disabled={!contactPhone}
                  data-testid="channel-sms"
                />
                <MessageSquare className="h-4 w-4 text-gray-500" />
                <span className={!contactPhone ? 'text-gray-400' : ''}>
                  SMS
                  {!contactPhone && <span className="text-xs ml-1">(no phone)</span>}
                </span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-gray-50">
                <Checkbox
                  checked={channels.whatsapp}
                  onCheckedChange={(checked) => setChannels(prev => ({ ...prev, whatsapp: checked }))}
                  disabled={!contactPhone}
                  data-testid="channel-whatsapp"
                />
                <Phone className="h-4 w-4 text-green-500" />
                <span className={!contactPhone ? 'text-gray-400' : ''}>
                  WhatsApp
                  {!contactPhone && <span className="text-xs ml-1">(no phone)</span>}
                </span>
              </label>
            </div>
          </div>

          {/* Result Display */}
          {result && (
            <div className="space-y-3 border-t pt-4">
              {result.portal_url && (
                <div>
                  <Label className="text-xs text-gray-500">Portal Link:</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      value={result.portal_url}
                      readOnly
                      className="text-xs"
                      data-testid="portal-url-input"
                    />
                    <Button
                      size="icon"
                      variant="outline"
                      onClick={() => copyToClipboard(result.portal_url)}
                      data-testid="copy-link-button"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="outline"
                      onClick={() => window.open(result.portal_url, '_blank')}
                      data-testid="open-link-button"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                {result.results?.sent?.map(channel => (
                  <Badge key={channel} className="bg-green-100 text-green-700">
                    <Check className="h-3 w-3 mr-1" />
                    {channel}
                  </Badge>
                ))}
                {result.results?.failed?.map(fail => (
                  <Badge key={fail.channel} variant="destructive">
                    {fail.channel}: {fail.error?.slice(0, 30)}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button
            onClick={handleSend}
            disabled={sending}
            className="bg-violet-600 hover:bg-violet-700"
            data-testid="send-link-button"
          >
            {sending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Send Link
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
